import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router";

import {
  Alert,
  Button,
  Card,
  LoadingBlock,
  PageHeader,
  SelectField,
  TextField,
} from "../../components/ui";
import { useToast } from "../../components/Toast";
import { api, applyFieldErrors, errorMessage } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useConfig } from "../../lib/config";
import type { Profile } from "../orders/types";

type ProfileForm = Omit<Profile, "email">;

export function ProfilePage() {
  const { data: config } = useConfig();
  const { refresh } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const profile = useQuery({ queryKey: ["profile"], queryFn: () => api.get<Profile>("/profile") });
  const {
    register,
    handleSubmit,
    reset,
    setError: setFieldError,
    formState,
  } = useForm<ProfileForm>();

  useEffect(() => {
    if (profile.data) reset(profile.data);
  }, [profile.data, reset]);

  if (profile.isLoading || !config) return <LoadingBlock />;
  const e = formState.errors;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="My account"
        description={
          <>
            Your details are used to fill in new orders. Change your password on the{" "}
            <Link className="text-brand-700 underline" to="/account/security">
              sign-in and security
            </Link>{" "}
            page.
          </>
        }
      />
      <Card>
        <form
          noValidate
          className="grid gap-4 sm:grid-cols-2"
          onSubmit={handleSubmit(async (values) => {
            setError(null);
            const body = Object.fromEntries(
              Object.entries(values).map(([k, v]) => [k, v === "" ? null : v]),
            );
            try {
              await api.put("/profile", body);
              await qc.invalidateQueries({ queryKey: ["profile"] });
              await refresh();
              notify("Your details were saved.");
            } catch (err) {
              if (!applyFieldErrors(err, setFieldError)) setError(errorMessage(err));
            }
          })}
        >
          {error && (
            <div className="sm:col-span-2">
              <Alert tone="error">{error}</Alert>
            </div>
          )}
          <p className="text-sm text-ink-muted sm:col-span-2">
            Sign-in email: {profile.data?.email}
          </p>
          <TextField
            label="First name"
            required
            error={e.first_name?.message}
            {...register("first_name", { required: "Enter your first name." })}
          />
          <TextField
            label="Last name"
            required
            error={e.last_name?.message}
            {...register("last_name", { required: "Enter your last name." })}
          />
          <TextField
            label="Name on orders"
            required
            error={e.customer_name?.message}
            {...register("customer_name", { required: "Enter the name for orders." })}
          />
          <TextField
            label="Company name"
            error={e.company_name?.message}
            {...register("company_name")}
          />
          <TextField label="Phone" type="tel" error={e.phone?.message} {...register("phone")} />
          <TextField
            label="Contact email for orders"
            type="email"
            error={e.contact_email?.message}
            {...register("contact_email")}
          />
          <div className="sm:col-span-2">
            <TextField label="Street address" error={e.street?.message} {...register("street")} />
          </div>
          <TextField label="City" error={e.city?.message} {...register("city")} />
          <div className="grid grid-cols-2 gap-4">
            <TextField label="State" error={e.state?.message} {...register("state")} />
            <TextField label="ZIP" error={e.zip_code?.message} {...register("zip_code")} />
          </div>
          <SelectField
            label="Usual delivery method"
            options={config.options.delivery_methods}
            {...register("default_delivery_method")}
          />
          <SelectField
            label="Usual payment"
            options={config.options.payment_methods}
            {...register("default_payment_method")}
          />
          <div className="sm:col-span-2">
            <Button type="submit" busy={formState.isSubmitting}>
              Save
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
